import { useEffect } from "react";
import { useWatchPosition } from "../../../hooks/useGeolocation";
import { calcDistance, formatDistance } from "../../../utils/distance";
import type { UserRoute } from "../../../hooks/useMyRoutes";
import type { RouteStop } from "../../../hooks/useMyRoutes";

interface Props {
  route: UserRoute;
  onClose: () => void;
  onStopTracking: () => void;
  onCheckComplete: (venueId: string, lat: number, lng: number) => Promise<boolean>;
  onVenueCompleted?: (stop: RouteStop) => void;
}

export default function RouteTrackerModal({ route, onClose, onStopTracking, onCheckComplete, onVenueCompleted }: Props) {
  const { status, lat, lng, startWatching, stopWatching } = useWatchPosition();

  useEffect(() => {
    startWatching();
    return () => stopWatching();
  }, [startWatching, stopWatching]);

  useEffect(() => {
    if (lat === null || lng === null) return;
    route.stops.forEach(async (stop) => {
      if (!stop.completed) {
        const wasCompleted = await onCheckComplete(stop.venueId, lat, lng);
        if (wasCompleted && onVenueCompleted) {
          onVenueCompleted(stop);
        }
      }
    });
  }, [lat, lng, route.stops, onCheckComplete, onVenueCompleted]);

  const completed = route.stops.filter((s) => s.completed).length;
  const total = route.stops.length;
  const progress = total > 0 ? (completed / total) * 100 : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-stone-100">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="font-bold text-stone-900">{route.name}</h2>
              <p className="text-xs text-stone-500">{completed}/{total} mekan tamamlandı</p>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-600 rounded-lg hover:bg-stone-100 cursor-pointer">
              <i className="ri-close-line" />
            </button>
          </div>
          {/* Progress bar */}
          <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 rounded-full transition-all duration-700" style={{ width: `${progress}%` }} />
          </div>
          {/* Location status */}
          <div className="flex items-center gap-2 mt-2">
            <span className={`w-2 h-2 rounded-full ${status === "success" ? "bg-emerald-400 animate-pulse" : status === "loading" ? "bg-amber-400 animate-pulse" : "bg-red-400"}`} />
            <span className="text-xs text-stone-500">
              {status === "success" ? "Konum takip ediliyor" : status === "loading" ? "Konum alınıyor..." : "Konum alınamadı"}
            </span>
          </div>
        </div>

        {/* Stops list */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {route.stops.map((stop, idx) => {
            const dist = lat && lng ? calcDistance(lat, lng, stop.lat, stop.lng) : null;
            const isNext = !stop.completed && route.stops.filter((s) => !s.completed)[0]?.venueId === stop.venueId;
            return (
              <div key={stop.venueId}
                className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${stop.completed ? "border-emerald-200 bg-emerald-50 opacity-70" : isNext ? "border-amber-300 bg-amber-50" : "border-stone-100 bg-white"}`}>
                <div className="relative flex-shrink-0">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${stop.completed ? "bg-emerald-500 text-white" : isNext ? "bg-amber-500 text-white" : "bg-stone-200 text-stone-600"}`}>
                    {stop.completed ? <i className="ri-check-line" /> : idx + 1}
                  </div>
                </div>
                <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
                  <img src={stop.image} alt={stop.venueName} className="w-full h-full object-cover object-top" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`font-semibold text-sm truncate ${stop.completed ? "line-through text-stone-400" : "text-stone-800"}`}>{stop.venueName}</p>
                  <p className="text-xs text-stone-500">{stop.district}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  {stop.completed ? (
                    <span className="text-xs text-emerald-600 font-semibold">✓ Tamamlandı</span>
                  ) : dist !== null ? (
                    <div>
                      <span className={`text-xs font-bold ${dist <= 100 ? "text-emerald-600" : "text-stone-600"}`}>{formatDistance(dist)}</span>
                      {dist <= 100 && <p className="text-xs text-emerald-500">Yakın!</p>}
                    </div>
                  ) : <span className="text-xs text-stone-400">—</span>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-stone-100">
          {completed === total && total > 0 ? (
            <div className="text-center">
              <p className="text-emerald-700 font-bold mb-3">🎉 Tebrikler! Rotayı tamamladınız!</p>
              <button onClick={() => { onStopTracking(); onClose(); }}
                className="px-6 py-2.5 bg-emerald-700 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap w-full">
                Rotayı Bitir
              </button>
            </div>
          ) : (
            <button onClick={() => { onStopTracking(); onClose(); }}
              className="w-full px-6 py-2.5 border border-stone-300 text-stone-700 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap hover:bg-stone-50 transition-colors">
              Takibi Durdur
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
